import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from "../../common/withRouter";
import { ButtonOBSOLETED } from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DisplayGuid } from "../items";

// Admin overview of the weekly SALTO master codes — the code support hands out to
// users who cannot get into a facility. One per facility, auto-rotated every Monday;
// can also be regenerated on demand here (e.g. to invalidate a leaked code).
const MasterCodesInternal = (props) => {
  const [rows, setRows] = useState(null);
  const [busyFacilityId, setBusyFacilityId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const response = await props.api.saltoMasterCodes.getMasters();
    if (response.success) {
      setRows(response.data);
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? "-" : date.toLocaleString();
  };

  const regenerate = async (facilityId) => {
    setBusyFacilityId(facilityId);
    await props.api.saltoMasterCodes.rotateMaster(facilityId);
    await loadData();
    setBusyFacilityId(null);
  };

  const regenerateAll = async () => {
    if (!confirm("Regenerate the master code for ALL SALTO facilities?")) {
      return;
    }
    setBusyAll(true);
    await props.api.saltoMasterCodes.rotateAllMasters();
    await loadData();
    setBusyAll(false);
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <div className="content-with-right-pane-delimiter">
            <PageTop
              documentTitle="Salto master codes"
              documentDescription="Weekly master door codes per SALTO facility"
            />
            <h2 className="main-page-header">Salto master codes</h2>
            <p>
              Weekly master code per SALTO facility, for users who cannot get in.
              Rotated automatically every Monday; use Regenerate to bootstrap a
              facility or to invalidate a leaked code immediately.
            </p>
            {rows == null ? (
              <span>Loading...</span>
            ) : (
              <>
                <br />
                <div className="filters-submit-wrapper">
                  <ButtonOBSOLETED
                    onClick={regenerateAll}
                    type="submit"
                    buttonContent={busyAll ? "Regenerating..." : "Regenerate all"}
                    disabled={busyAll}
                  />
                </div>
                <br />
                <article className="data-table-wrap">
                  <table className="data-lists component-data-table cell-content-align-top">
                    <thead>
                      <tr>
                        <td>Facility</td>
                        <td>Master code</td>
                        <td>Valid until</td>
                        <td>Action</td>
                      </tr>
                    </thead>
                    {rows.map((row) => (
                      <tbody key={row.facilityId}>
                        <tr>
                          <td>
                            <strong className="table-cell-label">Facility</strong>
                            {row.facilityName} -{" "}
                            <DisplayGuid short>{row.facilityId}</DisplayGuid>
                          </td>
                          <td>
                            <strong className="table-cell-label">Master code</strong>
                            {row.code ? (
                              <span style={{ fontSize: "1.4em", fontWeight: "bold", letterSpacing: "2px" }}>
                                {row.code}
                              </span>
                            ) : (
                              <span style={{ color: "#b00" }}>none</span>
                            )}
                          </td>
                          <td>
                            <strong className="table-cell-label">Valid until</strong>
                            {formatDate(row.endTimeUtc)}
                          </td>
                          <td>
                            <strong className="table-cell-label">Action</strong>
                            <button
                              disabled={busyFacilityId === row.facilityId}
                              onClick={() => regenerate(row.facilityId)}
                            >
                              {busyFacilityId === row.facilityId ? "..." : "Regenerate"}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    ))}
                  </table>
                </article>
              </>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const MasterCodes = withRouter(
  withApi(
    connect(
      ({ user, categories }) => ({ isLoggedIn: !!user, categories }),
      {}
    )(MasterCodesInternal)
  )
);

MasterCodes.requiresLogin = true;
MasterCodes.propTypes = { isLoggedIn: PropTypes.bool };

export default MasterCodes;
